/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect } from 'react';
import { useStaticAsset } from '@leagueplatform/asset-config';
import {
  Box,
  GDSStyleObject,
  UtilityText,
  StackItem,
  Button,
  StackLayout,
  useMediaQuery,
  queryHelpers,
  Tooltip,
} from '@leagueplatform/genesis-core';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { getPartnerAccessToken } from '@leagueplatform/sso-util';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { COB_LIBRARY_ASSET_KEYS } from '../../types/cob-library.types';
import { SuccessModal } from './sucessModal';
import { ConfirmationModal } from './confirmationModal';
import COBQuestioner from '../../assets/json/COB_QUESTIONER.json';
import './multistep.css';
import useCobStore from '../../store/cob-store';
import { cobLibrary } from '../../cob-library.config';
import { SubmitErrorModal } from './submitErrorModal';

export function FooterMultiStepProgress(props: any) {
  const [isSubmitErrorModal, setIsSubmitErrorModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [isSuccessModal, setIsSuccessModal] = useState<boolean>(false);

  const {
    handleFormOrNext,
    isValidateForm,
    list,
    closeForm,
    checkNoNext,
    isNextDisabled,
    isChildRef,
    isValidPCN,
  } = props;
  const [isNoNext, setNoNext] = useState(checkNoNext);
  const totalPageCount = list?.length;
  const cobPayload = useCobStore((state: any) => state.cobInformation);
  const demographicInfo = useCobStore((state: any) => state.demographics);
  const config = cobLibrary.getConfig();
  const { HIGHMARK_API_URL, useCurrentRegionUbkHeader } = config;
  const currentUbk: any = useCurrentRegionUbkHeader() || null;
  const ubkValue = currentUbk?.headers?.Brandubk || null;
  const ref = React.createRef();
  const tallerThanSE = useMediaQuery('(max-height: 667px)');
  const isBelow400 = useMediaQuery(queryHelpers.down(400));
  const is360 = useMediaQuery(queryHelpers.down(360));
  const is355 = useMediaQuery(queryHelpers.between([300, 359]));

  const rightArrowDisabledIcon = useStaticAsset(
    COB_LIBRARY_ASSET_KEYS.RIGHT_ARROW_DISABLED_ICON,
  );
  const rightArrowIcon = useStaticAsset(
    COB_LIBRARY_ASSET_KEYS.RIGHT_ARROW_ICON,
  );
  const leftArrowDisabledIcon = useStaticAsset(
    COB_LIBRARY_ASSET_KEYS.LEFT_ARROW_DISABLED_ICON,
  );
  const leftArrowIcon = useStaticAsset(COB_LIBRARY_ASSET_KEYS.LEFT_ARROW_ICON);

  const navigate = () => {
    if (
      localStorage.getItem(isChildRef.current.getDependant()) === 'true' &&
      selectedIndex !== list.length - 1
    ) {
      setSelectedIndex(selectedIndex + 1);
      isValidateForm(false);
    }
  };

  // eslint-disable-next-line consistent-return
  const goNext = () => {
    if (isNextDisabled) {
      return false;
    }
    if (handleFormOrNext) {
      if (!isNoNext) {
        if (list[selectedIndex].props.partPCNBIN === 'true' && isValidPCN) {
          isValidateForm(true);
          if (isChildRef?.current) {
            isChildRef.current?.checkPCNBIN();
            setTimeout(() => {
              navigate();
            }, 3000);
          }
        } else if (selectedIndex !== list.length - 1) {
          setSelectedIndex(selectedIndex + 1);
          isValidateForm(false);
        }
      }
    } else {
      isValidateForm(true);
      if (isChildRef?.current) {
        isChildRef.current?.callValidation();
      }
    }
  };

  const goPrevious = () => {
    if (selectedIndex !== 0) {
      setSelectedIndex(selectedIndex - 1);
    }
    isValidateForm(false);
  };

  const trackAnalytics = (name: string) => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      detail: name,
      screen_name: 'COB',
      path: window.location.pathname,
      event_author: 'engen',
    });
  };

  async function handleFormSubmit() {
    if (handleFormOrNext) {
      if (
        localStorage.getItem('pcnValidationSuccess') === '' ||
        localStorage.getItem('pcnValidationSuccess') === 'false'
      ) {
        if (isChildRef?.current?.checkPCNBIN) {
          isChildRef.current?.checkPCNBIN();
        }
      }
      setTimeout(async () => {
        if (
          localStorage.getItem('pcnValidationSuccess') === 'true' ||
          isChildRef?.current?.checkPCNBIN === undefined
        ) {
          setIsLoading(true);
          const userData = cobPayload;
          const token = await getPartnerAccessToken('highmark');
          const groupNumber = localStorage.getItem('groupNumber');
          userData.groupNumber = groupNumber;
          localStorage.setItem('pcnValidationSuccess', '');
          try {
            const url = `${HIGHMARK_API_URL}/digital/v2/members/coordination-of-benefits/form`;
            if (token) {
              const response = await fetch(url, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  ...(ubkValue && { brandUbk: ubkValue }),
                },
                body: JSON.stringify(userData),
              });
              const data = response ? await response.json() : '';
              setIsLoading(false);
              if (
                Object.keys(data) &&
                Object.keys(data).length !== 0 &&
                data?.status !== '400'
              ) {
                setIsSubmitErrorModal(false);
                localStorage.setItem('pcnError', '');
                setIsSuccessModal(true);
              } else {
                if (
                  isChildRef !== undefined &&
                  data.errorMessage.includes('Invalid Bin and PCN')
                ) {
                  localStorage.setItem('pcnError', data.errorMessage);
                  isChildRef.current.callValidation();
                }
                setIsSubmitErrorModal(true);
              }
            }
          } catch (error: any) {
            setIsLoading(false);
            setIsSubmitErrorModal(true);
          }
        }
      }, 2000);
    } else {
      isValidateForm(true);
      if (isChildRef?.current) {
        isChildRef.current?.callValidation();
      }
    }
  }

  const prependTooltipRightArrow = (nextDisabled: any) => {
    const generateRightButton = (
      <Button
        priority="secondary"
        quiet
        size="toolbar"
        tabIndex={0}
        onClick={goNext}
        css={{
          width: 'fit-content !important',
          padding: '0px !important',
          '&:focus': {
            outline: 'none !important',
          },
          '&:focus-visible': {
            outline: '1px solid !important',
          },
        }}
      >
        <img
          style={{ cursor: 'pointer', marginRight: '1px' }}
          aria-hidden="true"
          src={
            nextDisabled
              ? (rightArrowDisabledIcon as string)
              : (rightArrowIcon as string)
          }
          alt="right arrow icon"
          width="24px"
        />
      </Button>
    );
    if (nextDisabled) {
      return generateRightButton;
    }
    return (
      <Tooltip align="start" side="top" avoidCollisions content="next">
        {generateRightButton}
      </Tooltip>
    );
  };

  const prependTooltipLeftArrow = (selectedListIndex: number) => {
    const generateLeftButton = (
      <Button
        priority="secondary"
        quiet
        size="toolbar"
        tabIndex={
          selectedIndex < 1 && list[selectedListIndex].props.startNow === 'true'
            ? 1
            : 0
        }
        onClick={selectedListIndex + 1 > 1 ? goPrevious : () => {}}
        css={{
          width: 'fit-content !important',
          padding: '0px !important',
          '&:focus': {
            outline: 'none !important',
          },
          '&:focus-visible': {
            outline:
              selectedIndex < 1 &&
              list[selectedListIndex].props.startNow === 'true'
                ? 'none !important'
                : '1px solid !important',
          },
        }}
      >
        <img
          style={{
            display:
              selectedIndex < 1 &&
              list[selectedListIndex].props.startNow === 'true'
                ? 'none'
                : 'block',
            cursor: selectedListIndex + 1 > 1 ? 'pointer' : '',
          }}
          src={
            selectedListIndex + 1 > 1 &&
            list[selectedListIndex].props.startNow !== 'true'
              ? (leftArrowIcon as string)
              : (leftArrowDisabledIcon as string)
          }
          alt="left arrow icon"
          width="24px"
          aria-hidden="true"
        />
      </Button>
    );
    if (list[selectedIndex].props.startNow === 'true') {
      return generateLeftButton;
    }
    return (
      <Tooltip align="start" side="top" avoidCollisions content="previous">
        {generateLeftButton}
      </Tooltip>
    );
  };

  // eslint-disable-next-line consistent-return
  const renderContent = () => {
    if (
      checkNoNext &&
      list[selectedIndex].props.isMedicareDept === undefined &&
      list[selectedIndex].props.isMedicareNoDept !== 'true' &&
      list[selectedIndex].props.moveNextOrSubmit === 'false'
    ) {
      return (
        <Button
          onClick={(e: any) => {
            e.target.setAttribute('disabled', true);
            handleFormSubmit();
          }}
          size="small"
          css={{
            color: '$primaryTextDefault',
            cursor: 'pointer',
            backgroundColor: '$primaryBackgroundDefault',
            fontWeight: '$semibold',
            height: '48px',
            width: '138px',
            justifyContent: 'center',
            fontSize: '$bodyOne',
          }}
        >
          {COBQuestioner.COB.Buttons.SUBMIT}
        </Button>
      );
    }
    if (selectedIndex === 0 && list[selectedIndex].props.startNow === 'true') {
      return (
        <Button
          onClick={() => {
            goNext();
            trackAnalytics(COBQuestioner.COB.Buttons.STARTNOW);
          }}
          size="small"
          css={{
            color: '$primaryTextDefault',
            cursor: 'pointer',
            backgroundColor: '$primaryBackgroundDefault',
            marginRight: '10px',
            height: '48px',
            width: '134px',
            justifyContent: 'center',
            fontWeight: '$semibold',
            fontSize: '$bodyOne',
            // '@desktop': {
            //   marginRight: '6px',
            // },
            '@mobile': {
              marginRight: isBelow400 ? '2px' : '12px',
            },
          }}
          disabled={Object.keys(demographicInfo).length === 0}
        >
          {COBQuestioner.COB.Buttons.STARTNOW}
        </Button>
      );
    }

    if (list[selectedIndex].props.isMedicareDept === 'true' && checkNoNext) {
      return (
        <Button
          onClick={(e: any) => {
            e.target.setAttribute('disabled', true);
            handleFormSubmit();
          }}
          size="small"
          css={{
            color: '$primaryTextDefault',
            cursor: 'pointer',
            backgroundColor: '$primaryBackgroundDefault',
            width: '134px',
            height: '48px',
            justifyContent: 'center',
            fontWeight: '$semibold',
            fontSize: '$bodyOne',
          }}
        >
          {COBQuestioner.COB.Buttons.SUBMIT}
        </Button>
      );
    }

    if (list[selectedIndex].props.isMedicareDept === 'true' && !checkNoNext) {
      return (
        <Button
          onClick={goNext}
          size="small"
          css={{
            color: '$primaryTextDefault',
            cursor: 'pointer',
            backgroundColor: '$primaryBackgroundDefault',
            // marginRight: '15px',
            width: '134px',
            height: '48px',
            justifyContent: 'center',
            fontWeight: '$semibold',
            fontSize: '$bodyOne',
          }}
        >
          {COBQuestioner.COB.Buttons.SUBMIT}
        </Button>
      );
    }

    if (
      list[selectedIndex].props.isMedicareDept === undefined &&
      !checkNoNext &&
      !isNoNext &&
      list[selectedIndex].props.isMedicareDept !== 'true' &&
      list[selectedIndex]?.props?.isMedicareNoDept !== 'true' &&
      list[selectedIndex]?.props?.moveNextOrSubmit !== 'true'
    ) {
      return prependTooltipRightArrow(isNextDisabled);
    }

    if (
      list[selectedIndex].props.isOtherNoDept === 'true' &&
      list[selectedIndex].props.currentValue === '2' &&
      !isNoNext
    ) {
      return prependTooltipRightArrow(isNextDisabled);
    }
    if (list[selectedIndex].props.isMedicareNoDept === 'true') {
      return list[selectedIndex].props.isMedicareNoDept === 'true' &&
        checkNoNext &&
        isNoNext ? (
        <Button
          onClick={(e: any) => {
            e.target.setAttribute('disabled', true);
            handleFormSubmit();
          }}
          size="small"
          css={{
            color: '$primaryTextDefault',
            cursor: 'pointer',
            backgroundColor: '$primaryBackgroundDefault',
            width: '134px',
            height: '48px',
            justifyContent: 'center',
            fontWeight: '$semibold',
            fontSize: '$bodyOne',
          }}
        >
          {COBQuestioner.COB.Buttons.SUBMIT}
        </Button>
      ) : (
        <Button
          onClick={goNext}
          size="small"
          css={{
            color: '$primaryTextDefault',
            cursor: 'pointer',
            backgroundColor: '$primaryBackgroundDefault',
            width: '134px',
            height: '48px',
            justifyContent: 'center',
            fontWeight: '$semibold',
            fontSize: '$bodyOne',
          }}
          disabled={isNextDisabled}
        >
          {COBQuestioner.COB.Buttons.SUBMIT}
        </Button>
      );
    }
  };

  useEffect(() => {
    setNoNext(checkNoNext);
  }, [checkNoNext, isSuccessModal]);

  const checkResolution = () => {
    if (is355) {
      return '134px';
    }
    if (is360) {
      return '92px';
    }
    return '132px';
  };

  const checkpadding = () => {
    if (is355) {
      return '15px';
    }
    if (is360) {
      return '25px';
    }
    return '17px';
  };

  const setRightPadding = () => {
    if (is355) {
      return '15px';
    }
    if (is360) {
      return '25px';
    }
    return '17px';
  };

  const closeCOBFlow = () => {
    const checkElementAvailable = document.getElementById('closeCOBFlow');
    if (checkElementAvailable) {
      checkElementAvailable.style.display = 'none';
    }
  };
  return (
    <div>
      <Box
        css={{
          '@mobile': {
            width: '100vw',
          },
        }}
      >
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <>
            {isSubmitErrorModal && (
              <SubmitErrorModal onSubmitClose="true" ref={ref} />
            )}
            {isSuccessModal && (
              <>
                <Box
                  css={
                    {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      justifyContent: 'flex-end',
                      position: 'relative',
                      marginBottom: '$quarter',
                      marginTop: list[selectedIndex]?.props?.hideClose
                        ? '0px'
                        : '25px',
                      height: list[selectedIndex]?.props?.hideClose
                        ? '0px'
                        : '30px',
                      '@desktop': {
                        width: '100%',
                        height: '60px',
                        left: '680px',
                      },
                      '@mobile': {
                        width: '100%',
                        marginRight: isBelow400 ? '36px' : '20px',
                      },
                    } as GDSStyleObject
                  }
                >
                  <ConfirmationModal hideClose={false} successModal showClose />
                </Box>
                {closeCOBFlow()}
                <SuccessModal />
              </>
            )}
            {!isSuccessModal && (
              <>
                <Box
                  id="closeCOBFlow"
                  css={
                    {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      justifyContent: 'flex-end',
                      position: 'relative',
                      marginBottom: '$quarter',
                      marginTop: list[selectedIndex]?.props?.hideClose
                        ? '0px'
                        : '25px',
                      height: list[selectedIndex]?.props?.hideClose
                        ? '0px'
                        : '30px',
                      '@desktop': { width: '100%', left: '710px' },
                      '@mobile': { width: '100%' },
                    } as GDSStyleObject
                  }
                >
                  <ConfirmationModal
                    onClose={closeForm}
                    hideClose={Boolean(list[selectedIndex]?.props?.hideClose)}
                  />
                </Box>
                {/* {!isErrorModal && (retryCount.current <= 3) && (
            <ErrorModal onClose={closeWholeForm} />
          )
         
      }  */}

                <div>{list[selectedIndex]}</div>
                <div>{list[selectedIndex]?.showFooter}</div>
                {list[selectedIndex]?.props?.showFooter === 'true' ? (
                  <div
                    className={
                      list[selectedIndex].props.showFooter === 'true'
                        ? 'show'
                        : 'hidden'
                    }
                  >
                    <StackItem
                      css={
                        {
                          padding: '$none $none',
                          flexDirection: 'row',
                          marginTop: '$oneAndHalf',
                          justifyContent: 'center',
                          '@mobile': {
                            marginTop: '$one',
                            height: '24px',
                            paddingTop: tallerThanSE ? '50px' : '20px',
                          },
                        } as GDSStyleObject
                      }
                    >
                      {list[selectedIndex].props?.totalCount !== undefined &&
                        [...Array(list[selectedIndex].props.totalCount)].map(
                          (i: any, index: any) => (
                            <Box
                              // eslint-disable-next-line react/no-array-index-key
                              key={index}
                              css={{
                                backgroundColor:
                                  index >=
                                  Number(list[selectedIndex].props.pageNo)
                                    ? '$interactiveActionDisabled'
                                    : '$primaryBackgroundDefault',
                                '@desktop': {
                                  width:
                                    list[selectedIndex].props.totalCount > 20
                                      ? '4px'
                                      : '8px',
                                  maxWidth:
                                    list[selectedIndex].props.totalCount > 20
                                      ? '4px'
                                      : '8px',
                                },
                                '@mobile': {
                                  width:
                                    list[selectedIndex].props.totalCount > 20
                                      ? '2.5px'
                                      : '8px',
                                  maxWidth:
                                    list[selectedIndex].props.totalCount > 20
                                      ? '2.5px'
                                      : '8px',
                                },
                                height: '4px',
                                borderRadius: '$medium',
                                marginRight: '2px',
                              }}
                            />
                          ),
                        )}
                      {list[selectedIndex].props?.totalCount === undefined &&
                        [...Array(totalPageCount - 1)].map(
                          (i: any, index: any) => (
                            <Box
                              // eslint-disable-next-line react/no-array-index-key
                              key={index}
                              css={{
                                backgroundColor:
                                  index >=
                                  (list[list.length - 2].props.endValue !==
                                    '5' &&
                                  list[list.length - 2].props.endValue !== '3'
                                    ? selectedIndex
                                    : selectedIndex + 1)
                                    ? '$interactiveActionDisabled'
                                    : '$primaryBackgroundDefault',
                                '@desktop': {
                                  width: totalPageCount > 15 ? '6px' : '33.2px',
                                  maxWidth:
                                    totalPageCount > 15 ? '8px' : '33.2px',
                                },
                                '@mobile': {
                                  width: totalPageCount > 20 ? '4px' : '6px',
                                  maxWidth: totalPageCount > 20 ? '4px' : '6px',
                                },
                                height: '4px',
                                borderRadius: '$medium',
                                marginRight: '2px',
                              }}
                            />
                          ),
                        )}
                    </StackItem>

                    <Box
                      css={
                        {
                          display: 'flex',
                          flexDirection: 'row',
                          padding: '$none',
                          justifyContent: 'space-between',
                          height: '50px',
                          marginTop: '$two',
                          '@mobile': {
                            padding: '$quarter',
                          },
                        } as GDSStyleObject
                      }
                    >
                      <StackItem
                        css={
                          {
                            padding: '$none $quarter',
                            marginLeft: '0px',
                            '@desktop': {
                              width: '142px',
                            },
                            '@mobile': {
                              width: is360 ? '134px' : '132px',
                              paddingLeft: checkpadding(),
                            },
                          } as GDSStyleObject
                        }
                      >
                        {prependTooltipLeftArrow(selectedIndex)}
                      </StackItem>
                      <StackItem
                        css={
                          {
                            width: '100px',
                            textAlign: 'center',
                            margin: '0 auto',
                            paddingTop: selectedIndex < 1 ? '$half' : 'none',
                            paddingBottom: selectedIndex < 1 ? '$half' : 'none',
                          } as GDSStyleObject
                        }
                      >
                        <UtilityText
                          css={{
                            fontSize: '$overline',
                            margin: '0 auto',
                            display: list[selectedIndex]?.props?.pageNo
                              ? 'none'
                              : 'block',
                            '@desktop': {
                              paddingTop: '7px',
                            },
                            '@mobile': {
                              paddingTop: '2px',
                            },
                          }}
                        >
                          {list[0].props.startValue === '0'
                            ? selectedIndex
                            : selectedIndex + 1}{' '}
                          of{' '}
                          {list[list.length - 2].props.endValue === '5'
                            ? totalPageCount - 1
                            : totalPageCount - 1}
                        </UtilityText>
                        <UtilityText
                          css={{
                            fontSize: '$overline',
                            width: '50px',
                            margin: '0 auto',
                            display: list[selectedIndex]?.props?.pageNo
                              ? 'block'
                              : 'none',
                            '@desktop': {
                              paddingTop: '15px',
                            },
                            '@mobile': {
                              paddingTop: '5px',
                            },
                          }}
                        >
                          {list[selectedIndex].props.pageNo} of{' '}
                          {list[selectedIndex].props.totalCount}
                        </UtilityText>
                      </StackItem>

                      {selectedIndex + 1 === totalPageCount && !checkNoNext ? (
                        <StackItem
                          css={
                            {
                              padding: '$none $none',
                              '@desktop': {
                                width: '142px',
                              },
                              '@mobile': {
                                width: is360 ? '110px' : '112px',
                              },
                            } as GDSStyleObject
                          }
                        >
                          {isNextDisabled ? (
                            <Button
                              size="small"
                              css={{
                                color: '$primaryTextDefault',
                                cursor: 'pointer',
                                backgroundColor: '$primaryBackgroundDefault',
                                fontWeight: '$semibold',
                                height: '48px',
                                width: '134px',
                                justifyContent: 'center',
                                fontSize: '$bodyOne',
                              }}
                              disabled
                            >
                              {COBQuestioner.COB.Buttons.SUBMIT}
                            </Button>
                          ) : (
                            <Button
                              onClick={() => {
                                handleFormSubmit();
                                trackAnalytics(
                                  COBQuestioner.COB.Buttons.SUBMIT,
                                );
                              }}
                              size="small"
                              css={{
                                color: '$primaryTextDefault',
                                cursor: 'pointer',
                                backgroundColor: '$primaryBackgroundDefault',
                                width: '134px',
                                height: '48px',
                                justifyContent: 'center',
                                fontWeight: '$semibold',
                                fontSize: '$bodyOne',
                              }}
                            >
                              {COBQuestioner.COB.Buttons.SUBMIT}
                            </Button>
                          )}
                        </StackItem>
                      ) : (
                        <StackItem
                          css={
                            {
                              padding: '$none $none',
                              '@desktop': {
                                width: '142px',
                              },
                              '@mobile': {
                                width: checkResolution(),
                                paddingRight: setRightPadding(),
                              },
                              justifyContent: 'end',
                              alignItems: 'flex-end',
                            } as GDSStyleObject
                          }
                        >
                          {renderContent()}
                        </StackItem>
                      )}
                    </Box>
                  </div>
                ) : (
                  <StackLayout
                    css={
                      {
                        display:
                          list[selectedIndex]?.props?.showContinue === 'true'
                            ? 'block'
                            : 'none',
                        flexDirection: 'row',
                        padding: '$quarter',
                        justifyContent: 'space-between',
                      } as GDSStyleObject
                    }
                  >
                    <Box
                      css={
                        {
                          display: 'flex',
                          flexDirection: 'row',
                          padding: '$quarter',
                          justifyContent: 'space-between',
                        } as GDSStyleObject
                      }
                    >
                      <StackItem
                        css={
                          {
                            padding: '$none $four',
                            marginTop: '20px',
                          } as GDSStyleObject
                        }
                      >
                        {' '}
                      </StackItem>
                      <StackItem
                        css={
                          {
                            padding: '$none $three',
                          } as GDSStyleObject
                        }
                      >
                        {' '}
                      </StackItem>
                      <StackItem
                        css={
                          {
                            '@mobile': { padding: '$none $one' },
                            marginTop: '30px',
                          } as GDSStyleObject
                        }
                      >
                        <Button
                          onClick={() => {
                            goNext();
                            trackAnalytics(COBQuestioner.COB.Buttons.CONTINUE);
                          }}
                          size="small"
                          css={{
                            color: '$primaryTextDefault',
                            justifyContent: 'center',
                            height: '48px',
                            width: '134px',
                            marginRight: '8px',
                            cursor: 'pointer',
                            backgroundColor: '$primaryBackgroundDefault',
                            fontWeight: '$semibold',
                            fontSize: '$bodyOne',
                          }}
                        >
                          {COBQuestioner.COB.Buttons.CONTINUE}
                        </Button>
                      </StackItem>
                    </Box>
                  </StackLayout>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </div>
  );
}
